import { Helmet } from 'react-helmet-async';
import usePrerenderReady from '../hooks/usePrerenderReady';
import { SITE_URL } from '../seoConfig';
import { buildArticleSchema, buildBreadcrumbSchema, toJsonLd } from '../schema';

const GLOSSARY_TITLE = 'Fantasy Football Glossary — RosterRx';
const GLOSSARY_DESCRIPTION =
  'Plain-language definitions of common fantasy football terms: PPR, Standard scoring, consensus rankings, snake draft, waiver wire, bye week, and flex position.';
// Update by hand only when this page's terms/definitions actually change -- not the build date.
const GLOSSARY_LAST_UPDATED = '2026-07-27';
const ARTICLE_SCHEMA = buildArticleSchema({
  path: '/glossary',
  title: GLOSSARY_TITLE,
  description: GLOSSARY_DESCRIPTION,
  datePublished: GLOSSARY_LAST_UPDATED,
  dateModified: GLOSSARY_LAST_UPDATED,
});
const BREADCRUMB_SCHEMA = buildBreadcrumbSchema({ path: '/glossary', title: 'Glossary' });

/**
 * Target query/intent: "fantasy football glossary" / "what does PPR mean". Small, extensible
 * first set (R2.3) -- add more terms here over time rather than trying to be exhaustive at launch.
 */
const TERMS = [
  {
    term: 'PPR (Point Per Reception)',
    definition:
      'A scoring format that awards a fantasy point for every catch a player makes, in addition to yardage and touchdown points. PPR tends to boost the value of pass-catching running backs and possession receivers compared to Standard scoring.',
  },
  {
    term: 'Standard Scoring',
    definition:
      'A scoring format that awards points for yardage and touchdowns but nothing for receptions themselves. Standard leagues tend to value rushing and big-play receivers slightly more than PPR leagues do.',
  },
  {
    term: 'Consensus Rankings',
    definition:
      "A single ranking built by combining multiple experts' or sources' individual rankings for the same player pool. RosterRx's consensus view aggregates ESPN, Yahoo, and FantasyPros rankings into one list.",
  },
  {
    term: 'Snake Draft',
    definition:
      'A draft format where the pick order reverses each round (1st pick in round 1 picks last in round 2, and so on), so no single team is stuck picking last every round.',
  },
  {
    term: 'Waiver Wire',
    definition:
      'The pool of players not currently on any roster in your league. Adding a player from the waiver wire (or free agency) is the main way to improve your roster after the draft, usually by dropping an underperforming player to make room.',
  },
  {
    term: 'Bye Week',
    definition:
      "A week during the NFL regular season when a specific team doesn't play. Any player on that team is unavailable for fantasy scoring that week, which is why bye weeks matter when building a roster or setting a lineup.",
  },
  {
    term: 'Flex Position',
    definition:
      'A roster slot that can be filled by a player from more than one position — most commonly RB, WR, or TE — giving a lineup more flexibility than a strictly position-locked slot.',
  },
];

function GlossaryView() {
  usePrerenderReady();

  return (
    <div>
      <Helmet>
        <title>{GLOSSARY_TITLE}</title>
        <meta name='description' content={GLOSSARY_DESCRIPTION} />
        <link rel='canonical' href={`${SITE_URL}/glossary`} />
        <meta property='og:title' content={GLOSSARY_TITLE} />
        <meta property='og:description' content={GLOSSARY_DESCRIPTION} />
        <meta property='og:url' content={`${SITE_URL}/glossary`} />
        <meta property='og:type' content='website' />
        <meta name='twitter:card' content='summary' />
        <meta name='twitter:title' content={GLOSSARY_TITLE} />
        <meta name='twitter:description' content={GLOSSARY_DESCRIPTION} />
        <script type='application/ld+json'>{toJsonLd(ARTICLE_SCHEMA)}</script>
        <script type='application/ld+json'>{toJsonLd(BREADCRUMB_SCHEMA)}</script>
      </Helmet>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>Fantasy Football Glossary</h2>
        <p style={{ marginBottom: 16, color: 'var(--text-muted)' }}>
          Plain-language definitions of terms you'll run into building rankings, drafting, and
          managing a fantasy football roster.
        </p>
        {TERMS.map((entry) => (
          <div key={entry.term} style={{ marginBottom: 20 }}>
            <p style={{ fontWeight: 700, marginBottom: 4 }}>{entry.term}</p>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{entry.definition}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GlossaryView;
